/**
 * A single order in the merchant queue.
 *
 * Designed to be read at arm's length on a counter iPad and operated with a
 * thumb: the status and the time pressure occupy the top of the card, the
 * contents sit in the middle, and the next action is a full-width button at the
 * bottom. Everything a staff member needs to make a decision is above the fold.
 */

import { ActivityIndicator, Pressable, View } from 'react-native';
import {
  formatClock,
  CHIP_LABEL_KEYS,
  formatMoney,
  orderChips,
  orderStatusLabel,
  orderStatusTone,
  type OrderChip,
  formatDayTime,
  merchantActions,
  orderPressure,
  type MessageKey,
  type Order,
  type OrderStatus,
} from '@yummy/shared';
import {
  Button,
  Card,
  Elapsed,
  Row,
  Stack,
  Text,
  ToneBadge,
  Touchable,
  cn,
  useT,
  useTheme,
  type Tone,
} from '@yummy/ui-rnr';

/** Card edge tone, mapped to whole literal classes. */
const CARD_ACCENT = {
  neutral: 'border-l-subtle-foreground border-l-[3px]',
  brand: 'border-l-primary border-l-[3px]',
  warning: 'border-l-warning border-l-[3px]',
  success: 'border-l-success border-l-[3px]',
  danger: 'border-l-destructive border-l-[3px]',
} as const;

/**
 * The green fill for the one action that confirms a handover.
 *
 * Filling it with the success tone rather than the brand colour marks it as a
 * different kind of action from the transitions that come before it. The hover
 * and pressed states are restated because the button's own primary-coloured
 * states would otherwise win on interaction.
 */
const SUCCESS_BUTTON = 'bg-success hover:bg-success/90 active:bg-success/90';

export interface OrderCardProps {
  order: Order;
  busy?: boolean;
  onAdvance: (status: OrderStatus) => void;
  onOpen: () => void;
  /**
   * Records or clears the money, from the card.
   *
   * Offered on a ready order that is unpaid, because that is the moment the counter
   * has the cash in hand — the alternative is opening the sheet to say so, which is
   * four taps for something done at the pass.
   */
  onTogglePaid?: (order: Order) => void;
  /** True while this order's payment is being written, so its own button reports. */
  paying?: boolean;
  /** The chip doing the board's filtering, if any, so the card can mark it. */
  activeChip?: OrderChip | null;
  /** A chip was pressed: the board narrows by it, or clears it. */
  onChipPress?: (chip: OrderChip) => void;
  /** The status chip was pressed: the board switches to that status's tab. */
  onStatusPress?: () => void;
}

/**
 * The dictionary key for the label of an action `merchantActions` offers.
 *
 * `merchantActions` carries English labels because it is shared with callers
 * that have no translator; the card renders the localised key instead.
 *
 * @param to The status the action moves the order to.
 * @returns The key for that action's button label.
 */
function actionLabelKey(to: OrderStatus): MessageKey {
  if (to === 'preparing') return 'merchant.orders.startPreparing';
  if (to === 'ready') return 'merchant.orders.markReady';
  if (to === 'completed') return 'merchant.orders.handedOver';
  return 'merchant.orders.accept';
}

/**
 * How much pressure an order is under, which the card's edge and badge carry.
 *
 * `soon` is the lead before a deadline and `overdue` is past it; the two are
 * exclusive, so the colour means one thing at a time.
 */
/**
 * Which tone each filterable chip wears.
 *
 * The pressure ones come from the pressure they are under rather than from the
 * chip, which is why they are not in one table with the kinds.
 */
const CHIP_TONES: Record<OrderChip, Tone> = {
  pastEstimate: 'danger',
  waitingTooLong: 'danger',
  notCollected: 'danger',
  scheduled: 'info',
  bookedInsidePrep: 'warning',
};

/**
 * A card's chip, as something to press.
 *
 * The hairline is the affordance and the padding is the target: a chip is 24px and
 * a finger wants 44, so the press area grows vertically and the negative margin
 * takes the growth back out of the layout. Horizontally it grows by two points a
 * side, which is what a gap of eight allows without two targets touching.
 *
 * @param props Label, tone, and the press handler when there is one.
 * @returns The chip.
 */
function Chip({
  label,
  tone,
  emphasis = 'soft',
  onPress,
  active = false,
  accessibilityLabel,
}: {
  label: string;
  tone: Tone;
  emphasis?: 'soft' | 'solid';
  onPress?: () => void;
  active?: boolean;
  accessibilityLabel: string;
}) {
  const body = (
    <ToneBadge
      label={label}
      tone={tone}
      emphasis={emphasis}
      // `active` is the chip doing the filtering: ringed in the brand colour and
      // untinted, so it reads as chosen rather than as another kind of chip.
      className={cn(active ? 'border-primary bg-background' : 'border-border', 'border')}
    />
  );
  if (!onPress) return body;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      className="-mx-0.5 -my-2.5 px-0.5 py-2.5">
      {body}
    </Pressable>
  );
}

/**
 * Renders one order with its available actions.
 *
 * @param props Order, pending state, and handlers.
 * @returns The queue card.
 */
export function OrderCard({
  order,
  busy,
  onAdvance,
  onOpen,
  onTogglePaid,
  paying = false,
  activeChip = null,
  onChipPress,
  onStatusPress,
}: OrderCardProps) {
  const theme = useTheme();
  const t = useT();
  const pressure = orderPressure(order);
  const chips = orderChips(order);
  const isScheduled = order.is_scheduled;
  const actions = merchantActions(order.status);
  const [primary, ...secondary] = actions;
  const accent =
    order.status === 'ready'
      ? 'success'
      : pressure.pressure === 'overdue'
        ? 'danger'
        : pressure.pressure === 'soon'
          ? 'warning'
          : order.status === 'pending'
            ? 'brand'
            : 'neutral';

  const primaryLabel = primary ? t(actionLabelKey(primary.to)) : '';
  const detailsLabel = t('common.details');
  /**
   * Anything being written for this order blocks all of its buttons.
   *
   * Two writes can be in flight from one card — the status and the money — and only
   * one spinner should ever report, so the card is told which of the two is running
   * rather than sharing one flag between them.
   */
  const blocked = Boolean(busy) || paying;

  return (
    <Card className={cn('p-4', CARD_ACCENT[accent])}>
      <Stack gap="md">
        {/* Header: what state, how urgent ---------------------------------- */}
        <Row spread gap="md" align="start">
          {/* `shrink` as well as `min-w-0`: react-native-web defaults a View to
              `flex-shrink: 0`, so without both the left group holds its width and
              pushes the time off the right-hand edge of the card. */}
          <Stack gap="xs" className="min-w-0 shrink">
            <Row gap="sm" align="center" wrap>
              {/* Every chip on this card is the same size — the status used to be
                  the only one on the larger default while the exceptions took the
                  smaller, so the two read as different kinds of thing. */}
              {/* The status narrows by tab, so it is a button that switches tab
                  rather than a filter. */}
              <Chip
                label={orderStatusLabel(order.status, t)}
                tone={orderStatusTone(order.status)}
                emphasis={order.status === 'ready' ? 'solid' : 'soft'}
                onPress={onStatusPress}
                accessibilityLabel={t('merchant.orders.showStatusTab', {
                  status: orderStatusLabel(order.status, t),
                })}
              />
              {/* Everything else this order carries, from the one list the board
                  filters by — so a chip can never be shown and not offered. The
                  number is dropped: it is drawn beside the customer, not here. */}
              {chips.map((chip) => (
                <Chip
                  key={chip}
                  label={t(CHIP_LABEL_KEYS[chip])}
                  tone={CHIP_TONES[chip]}
                  onPress={onChipPress ? () => onChipPress(chip) : undefined}
                  active={activeChip === chip}
                  accessibilityLabel={t('merchant.orders.filterByChip', {
                    chip: t(CHIP_LABEL_KEYS[chip]),
                  })}
                />
              ))}
            </Row>
          </Stack>
          {/* The number and the money sit on the status line, where they are read:
              the number is what gets called across the counter, and the total is
              what gets charged at it. The board works the till as well as the
              kitchen, so the amount to collect cannot be a tap away. */}
          <Stack gap="xs" align="end" className="shrink-0">
            <Text className="text-3xl font-bold">{`#${order.order_number}`}</Text>
            <Text className="text-lg font-semibold tabular-nums">
              {formatMoney(order.total_cents, order.currency)}
            </Text>
            {/* The money's own fact, beside the money, and stated either way. A
                counter looks here for what to charge, and at that moment "nothing
                said" and "not paid" are the same thing — so the unpaid state is
                written rather than implied.

                Both states carry a tone, because both are things the counter acts on:
                green is settled, amber is money still owed. That does mean amber is
                the common colour on a board where most orders have not been paid yet
                — the board is a till as well as a kitchen, and what is owed is the
                thing it has to act on, so the common colour is the common job.

                Shaped exactly like the chips in the row across the card — the same
                `md` size, the same 1px border, the same radius — because the two are
                the same kind of thing and a smaller badge read as a lesser one. Not
                the row's `Chip`, though: that is a filter, and this is not a question
                the board can be asked.

                `self-end` because `ToneBadge` centres itself at the start of its
                parent, and this column is right-aligned: without it the chip sat
                short of the amount it belongs to. */}
            <ToneBadge
              label={
                order.paid_at ? t('merchant.orders.paid') : t('merchant.orders.unpaid')
              }
              tone={order.paid_at ? 'success' : 'warning'}
              className="border-border self-end border"
            />
          </Stack>
        </Row>

        {/* Identity ---------------------------------------------------------- */}

        <Touchable
          onPress={onOpen}
          accessibilityLabel={t('merchant.orders.openOrder', {
            number: `#${order.order_number}`,
          })}>
          <Row spread gap="md" align="end">
            {/* The identity column is the one that flexes. Without it the column is
                sized by its contents and holds that width — React Native's default
                `flex-shrink: 0` — so a long customer name beside the "Ordered from
                AU" chip pushed the clock past the card's own padding, where the card's
                `overflow-hidden` cut the end off "Ready by 17:20". Measured at three
                columns: the longest name plus the chip is 270 points against the 231
                the row has to share with the clock. Given the remainder the identity
                row wraps the chip instead — and only on the cards long enough to need
                it. `min-w-0` because the CSS default `min-width: auto` would refuse to
                shrink below the content anyway. */}
            <Stack gap="xs" className="min-w-0 flex-1">
              {/* The table beside the name, because for a hawker it is the
                  delivery instruction: who, and where it goes. */}
              {/* Centred, not baseline-aligned. The name and the table label are
                  the same size so the two are equivalent for them, but a chip is a
                  box with padding and no text baseline — CSS gives it its bottom
                  edge as one, so baseline alignment lifted it off the name. */}
              <Row gap="xs" align="center" wrap>
                <Text className="text-muted-foreground text-xs">{order.customer_name}</Text>
                {order.table_label ? (
                  <Text className="text-subtle-foreground text-xs">· {order.table_label}</Text>
                ) : null}
                {order.from_elsewhere ? (
                  <ToneBadge
                    label={t('merchant.orders.fromElsewhere', {
                      country: order.ordered_from_country ?? '?',
                    })}
                    tone="warning"
                  />
                ) : null}
              </Row>
              {/* The customer's record, above the phone because the phone is what it is
                    about. Red leads when there is a no-show, green alone when the
                    record is clean, and nothing at all for a first order — no
                    stranger is coloured by anyone else's record. */}
                {order.no_shows > 0 ? (
                  <Row gap="xs" align="center">
                    <View className="bg-destructive h-1.5 w-1.5 rounded-full" />
                    <Text className="text-destructive text-xs font-semibold">
                      {t('merchant.orders.noShows', { count: order.no_shows })}
                    </Text>
                    {order.collected > 0 ? (
                      <Text className="text-subtle-foreground text-xs">
                        · {t('merchant.orders.collected', { count: order.collected })}
                      </Text>
                    ) : null}
                  </Row>
                ) : order.collected > 0 ? (
                  <Row gap="xs" align="center">
                    <View className="bg-primary h-1.5 w-1.5 rounded-full" />
                    <Text className="text-primary text-xs font-semibold">
                      {t('merchant.orders.collected', { count: order.collected })}
                    </Text>
                  </Row>
                ) : null}
                <Text className="text-muted-foreground text-base">{order.customer_phone}</Text>
            </Stack>
            {/* One clock, counting from when the kitchen took the order on —
                with auto-accept that is the moment it was placed, so it starts
                on its own. A scheduled order shows when it is for instead, and
                the deadline it is judged against underneath. */}
            {isScheduled ? (
              <Stack gap="xs" align="end" className="shrink-0">
                <Text className="text-subtle-foreground text-base">
                  {formatDayTime(order.scheduled_for!, order.timezone, undefined, t)}
                </Text>
                {/* The number the kitchen plans around, in the same colours the
                    card's edge and badge carry. It was in the detail view only,
                    which is not where a counter reads a queue. */}
                {order.prep_start_at ? (
                  <Text
                    className={cn(
                      'text-xs',
                      pressure.pressure === 'overdue'
                        ? 'text-destructive font-semibold'
                        : pressure.pressure === 'soon'
                          ? 'text-warning font-semibold'
                          : 'text-subtle-foreground',
                    )}>
                    {t('merchant.orders.startBy', {
                      when: formatDayTime(order.prep_start_at, order.timezone, undefined, t),
                    })}
                  </Text>
                ) : null}
              </Stack>
            ) : order.accepted_at ? (
              <Stack gap="xs" align="end" className="shrink-0">
                {/* `variant` rather than a class on a wrapper: Elapsed renders its
                    own Text, and an inner class beats the outer one, so the size
                    has to be asked for. Its own colouring follows from the elapsed
                    time.

                    `until` on a closed order is what stops the clock. Without it a
                    collected order kept counting from its acceptance and turned red
                    fifteen minutes later — a live alarm on an order nobody can act
                    on, which is what the "Today" record is full of. The number it
                    freezes at is the time the order actually took. */}
                <Elapsed
                  since={order.accepted_at}
                  until={order.completed_at ?? order.cancelled_at ?? undefined}
                  format="bare"
                  variant="body"
                />
                {/* What a walk-up is actually judged against. `order-states.md`
                    splits the two kinds on this: a booked order's deadline is its
                    start-by, and a walk-up's is `eta_ready_at` — the quote, which
                    is re-based the moment it is accepted. The card named neither,
                    so "Past estimate" referred to a number only the detail held. */}
                <Text
                  className={cn(
                    'text-xs',
                    pressure.pressure === 'overdue'
                      ? 'text-destructive font-semibold'
                      : pressure.pressure === 'soon'
                        ? 'text-warning font-semibold'
                        : 'text-subtle-foreground',
                  )}>
                  {t('merchant.orders.readyBy', {
                    when: formatClock(order.eta_ready_at, order.timezone),
                  })}
                </Text>
              </Stack>
            ) : (
              // Nobody has accepted it, so there is no quote worth showing — it
              // is re-based on acceptance. The clock that means something is when
              // the customer landed, which is what "Waiting too long" counts from.
              <Text
                className={cn(
                  'shrink-0 text-xs',
                  pressure.pressure === 'overdue'
                    ? 'text-destructive font-semibold'
                    : 'text-subtle-foreground',
                )}>
                {t('merchant.orders.placedAt', {
                  when: formatClock(order.placed_at, order.timezone),
                })}
              </Text>
            )}
          </Row>
        </Touchable>

        {/* Contents ---------------------------------------------------------- */}

        {/* No money here: nothing is paid online, and the card is read for what
            to make. The line prices and the total live in the detail. */}
        <View className="bg-muted rounded-md p-3">
          <Stack gap="xs">
            {order.items.map((line) => (
              <Text key={line.id} className="text-base">
                <Text className="text-base font-bold">
                  {line.quantity}×{' '}
                </Text>
                {line.name}{line.variant_name_snapshot ? ` · ${line.variant_name_snapshot}` : ''}{(line.options ?? []).length > 0 ? ` · ${(line.options ?? []).map((option) => option.name).join(', ')}` : ''}
              </Text>
            ))}
          </Stack>
        </View>

        {order.note ? (
          <View className="border-l-warning border-l-[3px] pl-3">
            <Text className="text-warning text-xs font-semibold">
              {t('merchant.orders.note', { note: order.note })}
            </Text>
          </View>
        ) : null}

        {/* Actions ----------------------------------------------------------- */}

        {primary ? (
          <Stack gap="sm">
            <Button
              variant="default"
              size="lg"
              className={cn('w-full', primary.to === 'ready' && SUCCESS_BUTTON)}
              onPress={() => onAdvance(primary.to)}
              disabled={blocked}
              accessibilityLabel={primaryLabel}
              aria-busy={Boolean(busy)}>
              {busy ? (
                <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
              ) : (
                <Text>{primaryLabel}</Text>
              )}
            </Button>
            <Row gap="sm">
              {secondary.map((action) => {
                const label = t(actionLabelKey(action.to));
                return (
                  <Button
                    key={action.to}
                    variant="secondary"
                    className="flex-1"
                    onPress={() => onAdvance(action.to)}
                    disabled={blocked}
                    accessibilityLabel={label}>
                    <Text>{label}</Text>
                  </Button>
                );
              })}
              {/* The money, wherever the counter can still act on the order — which is
                  every status this branch draws, since a completed or cancelled one has
                  no primary action and falls to the row below. Payment is a fact about
                  the money, not a step in the kitchen's work: a customer can hand over
                  cash as the order is taken at the counter, while they wait, or as they
                  collect, and the till side of the board should not make them open a
                  sheet for any of the three.

                  Only while it is unpaid. A paid order has nothing left to do at the
                  pass, and the way back stays in the sheet with the rest of the order's
                  facts, where a mistake is worth undoing. */}
              {order.paid_at === null && onTogglePaid ? (
                <Button
                  variant="secondary"
                  className="flex-1"
                  onPress={() => onTogglePaid(order)}
                  disabled={blocked}
                  accessibilityLabel={t('merchant.orders.markPaid')}
                  aria-busy={paying}>
                  {paying ? (
                    <ActivityIndicator color={theme.palette.foreground} size="small" />
                  ) : (
                    <Text>{t('merchant.orders.markPaid')}</Text>
                  )}
                </Button>
              ) : null}
              <Button
                variant="ghost"
                onPress={onOpen}
                disabled={blocked}
                accessibilityLabel={detailsLabel}>
                <Text>{detailsLabel}</Text>
              </Button>
            </Row>
          </Stack>
        ) : (
          <Row spread gap="md" align="center">
            <Text className="text-subtle-foreground text-xs">
              {orderStatusLabel(order.status, t)} ·{' '}
              {formatDayTime(
                order.completed_at ?? order.cancelled_at ?? order.placed_at,
                order.timezone,
                undefined,
                t,
              )}
            </Text>
            <Button
              variant="ghost"
              size="sm"
              onPress={onOpen}
              accessibilityLabel={detailsLabel}>
              <Text>{detailsLabel}</Text>
            </Button>
          </Row>
        )}
      </Stack>
    </Card>
  );
}
